"use client";

import * as React from "react";
import Image from "next/image";
import { cn } from "@repo/utils/cn";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { VideoPlayerButton } from "./VideoPlayerButton";
import { AiDisclaimerForAlt } from "./AiDisclaimer";

export interface ImageWithAspectRatioProps {
  // Core image props from Storyblok
  src: string;
  /**
   * Nullable because CMS alt text is: a Storyblok asset with no alt yields `null`, and
   * several callers hold `alt?: string`. `parseAiAlt` already normalises nullish input to
   * an empty `cleanAlt`, and the sibling chokepoint `ResponsiveImage` has always accepted
   * `string | null`. Requiring a non-null string here only pushed callers into `|| ""` and
   * made the nullish path untestable.
   */
  alt?: string | null;
  aspectRatio?: string;
  width?: number;
  height?: number | "auto";
  quality?: number;
  // Optional Next.js Image props
  sizes?: string;
  priority?: boolean;
  fetchPriority?: "auto" | "high" | "low" | undefined;

  // Style
  className?: string;
  imageSpace?: "fill" | "fit";
  maxWidth?: number;
  maxHeight?: number;
  isFullWidth?: boolean;
  borderRadius?: string;
  horizontalAlignment?: "left" | "center" | "right";
  verticalAlignment?: "top" | "center" | "bottom";
  horizontalAlignmentMobile?: "left" | "center" | "right";
  verticalAlignmentMobile?: "top" | "center" | "bottom";

  // Video overlay
  showVideoButton?: boolean;
  videoId?: string;
  onVideoClick?: () => void;
  playButtonImage?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
}

export const ImageWithAspectRatio: React.FC<ImageWithAspectRatioProps> = ({
  src,
  alt,
  aspectRatio = "16/9",
  sizes,
  width = 420,
  height = 420,
  quality,
  priority = false,
  className,
  imageSpace = "fill",
  maxWidth = 420,
  maxHeight = 420,
  isFullWidth = false,
  borderRadius,
  horizontalAlignment = "center",
  verticalAlignment = "center",
  horizontalAlignmentMobile,
  verticalAlignmentMobile,
  showVideoButton = false,
  videoId,
  onVideoClick,
  playButtonImage,
  fetchPriority,
  ...props
}) => {
  const containerStyle: React.CSSProperties = {
    ...(borderRadius && { borderRadius }),
  };

  // The AI marker rides at the end of the alt text and must never reach the DOM.
  // Only `cleanAlt` is needed here now: whether a badge is due is decided by
  // `AiDisclaimerForAlt`, which also consults the marked-asset registry.
  const { cleanAlt } = parseAiAlt(alt);

  const isSvg = /\.svg($|\?)/i.test(src);

  if (aspectRatio === "1/1") {
    containerStyle.maxWidth = `${maxWidth}px`;
    containerStyle.maxHeight = `${maxHeight}px`;
  }

  if (isFullWidth) {
    containerStyle.maxWidth = "100%";
    containerStyle.width = "100%";
  }

  if (width === 420 && height === 420) {
    containerStyle.aspectRatio = aspectRatio;
  }

  const horizontalAlignmentClass = cn(
    // Desktop alignment
    horizontalAlignment === "left" && "uipm:md:justify-items-start",
    horizontalAlignment === "center" && "uipm:md:justify-items-center",
    horizontalAlignment === "right" && "uipm:md:justify-items-end",
    // Mobile alignment overrides
    horizontalAlignmentMobile === "left" && "uipm:justify-items-start",
    horizontalAlignmentMobile === "center" && "uipm:justify-items-center",
    horizontalAlignmentMobile === "right" && "uipm:justify-items-end",
  );

  const verticalAlignmentClass = cn(
    // Desktop alignment
    verticalAlignment === "top" && "uipm:md:content-start",
    verticalAlignment === "center" && "uipm:md:content-center",
    verticalAlignment === "bottom" && "uipm:md:content-end",
    // Mobile alignment overrides
    verticalAlignmentMobile === "top" && "uipm:content-start",
    verticalAlignmentMobile === "center" && "uipm:content-center",
    verticalAlignmentMobile === "bottom" && "uipm:content-end",
  );

  const imageClassName = cn(
    imageSpace === "fill"
      ? "uipm:object-cover uipm:w-full uipm:h-full"
      : "uipm:object-contain",
  );

  return (
    <div
      className={cn(
        "uipm:relative uipm:overflow-hidden uipm:w-full uipm:h-full",
        horizontalAlignmentClass,
        verticalAlignmentClass,
        className,
      )}
      style={containerStyle}
      {...props}
    >
      <Image
        src={src}
        alt={cleanAlt}
        sizes={sizes}
        priority={priority}
        fetchPriority={fetchPriority}
        quality={quality}
        width={width}
        height={height !== "auto" ? (height as number) : 420}
        className={imageClassName}
        objectFit={imageSpace === "fill" ? "cover" : "contain"}
        style={{
          borderRadius,
          width:
            imageSpace === "fill" ||
            (imageSpace === "fit" && width === 420) ||
            isFullWidth
              ? "100%"
              : "auto",
          height:
            isSvg ||
            imageSpace === "fill" ||
            (imageSpace === "fit" && width === 420 && height !== "auto")
              ? "100%"
              : "auto",
        }}
      />

      {showVideoButton && videoId && onVideoClick && (
        <VideoPlayerButton
          onClick={onVideoClick}
          playButtonImage={playButtonImage}
        />
      )}

      <AiDisclaimerForAlt alt={alt} src={src} />
    </div>
  );
};
